import { Row, Col, size } from '@everlywell/leaves';
import { GatsbyImage as Img } from 'gatsby-plugin-image';
import React, { useState } from 'react';
import { animated as A, useSpring, config } from 'react-spring';
import { generateKey } from 'utils/helpers';

import { useScrolledIntoViewOnce } from 'utils/hooks';
import { ContentWithImage } from 'utils/types';
import Container from '../Container';
import SectionDisplayText from '../SectionDisplayText';
import SectionTitle from '../SectionTitle';

import * as S from './styles';

export interface ExperienceSectionProps {
  title: string;
  headline: string;
  listOfContent: ContentWithImage[];
}

export const ExperienceSection: React.FC<ExperienceSectionProps> = ({
  title,
  headline,
  listOfContent,
}) => {
  const [inView, setInView] = useState(false);
  const [isTitleInView, setIsTitleInView] = useState(false);
  const [isHeadlineInView, setIsHeadlineInView] = useState(false);

  const contentCardsEl = useScrolledIntoViewOnce(() => {
    setInView(true);
  });

  const titleRef = useScrolledIntoViewOnce(() => {
    setIsTitleInView(true);
  });

  const headlineRef = useScrolledIntoViewOnce(() => {
    setIsHeadlineInView(true);
  });

  // all transitions triggered from scrolling into view
  const titleTransition = useSpring({
    from: { opacity: 0, transform: `translateY(${size.xl1}px)` },
    to: isTitleInView ? { opacity: 1, transform: 'translateY(0)' } : null,
    config: config.slow,
  });

  const displayTransition = useSpring({
    from: { opacity: 0, transform: `translateY(${size.xl2}px)` },
    to: isHeadlineInView ? { opacity: 1, transform: 'translateY(0)' } : null,
    config: config.slow,
  });

  const cardsTransition = useSpring({
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : `translateY(${size.xl3}px)`,
    config: config.slow,
  });

  return (
    <Container>
      <Row center="xs">
        <Col xs={12} lg={10}>
          <A.div style={titleTransition} ref={titleRef}>
            <SectionTitle text={title} />
          </A.div>
        </Col>
      </Row>
      <Row center="xs">
        <Col xs={12} lg={10}>
          <A.div style={displayTransition} ref={headlineRef}>
            <SectionDisplayText text={headline} />
          </A.div>
        </Col>
      </Row>
      <Row center="xs" around="lg">
        <Col xs={10} sm={12} lg={10}>
          <S.InfoBlockContainer ref={contentCardsEl}>
            {listOfContent.length
              ? listOfContent.map((content) => (
                  <S.AnimatedBlockContainer
                    style={cardsTransition}
                    key={generateKey()}
                  >
                    <S.InfoBlock
                      title={content.heading}
                      image={
                        <Img
                          image={content.image.gatsbyImageData}
                          alt={content.image.description || ''}
                          style={{ width: '100%', height: '100%' }}
                        />
                      }
                      imageAlt={content.image.description || ''}
                      content={content.description?.description || ''}
                      cta={content.cta}
                      size="large"
                    />
                  </S.AnimatedBlockContainer>
                ))
              : null}
          </S.InfoBlockContainer>
        </Col>
      </Row>
    </Container>
  );
};

export default ExperienceSection;
